import { deliverabilityRates, deriveCounters } from "./reporting.js";
function metricValue(metric, counters) {
    const r = deliverabilityRates(counters);
    switch (metric) {
        case "complaint_rate":
            return r.complaintRate;
        case "bounce_rate":
            return r.bounceRate;
        case "send_failures":
            // Absolute count of hard failures (bounces + complaints) for this window.
            return counters.bounces + counters.complaints;
        case "reputation":
            /**
             * A 0-100 health score, worse as it climbs — the same direction as every
             * other metric here, so one comparison works for all of them (#210).
             *
             * It used to return a hardcoded 0, which meant the rule could never breach
             * any threshold above zero: an operator could configure a reputation alert,
             * see it in the console, and it would never fire. An alert that cannot fire
             * is worse than an absent one, because its presence is read as coverage.
             *
             * This is derived from OUR counters, not from SES's account-level
             * reputation dashboard — that is per-account and lags by hours, so it can
             * neither be attributed to an org nor act in time to stop a send. What this
             * measures is the same thing SES would eventually punish, from data we hold
             * per campaign and in real time.
             *
             * Read as percentage points, so a threshold is legible: the weights place
             * **5% bounces and 0.1% complaints at the same score of 5**, which is the
             * industry equivalence — those are the two levels at which a provider
             * starts acting. That makes complaints fifty times heavier per event, which
             * they are.
             *
             * Capped at 100. Past that the number stops meaning anything; a send that
             * bad is already halted.
             */
            return Math.min(100, r.bounceRate * 100 + r.complaintRate * 5000);
    }
}
/** Evaluate an org's rules against a campaign's counters; worst level per metric. */
export function evaluateAlerts(config, counters) {
    const breaches = [];
    for (const rule of config.rules) {
        if (!rule.enabled)
            continue;
        const value = metricValue(rule.metric, counters);
        if (value >= rule.haltAt) {
            breaches.push({ metric: rule.metric, level: "critical", value, threshold: rule.haltAt });
        }
        else if (value >= rule.warnAt) {
            breaches.push({ metric: rule.metric, level: "warn", value, threshold: rule.warnAt });
        }
    }
    return breaches;
}
/**
 * Thresholds a newly provisioned org starts with (#217).
 *
 * A control that is off by default is not a control: before this, every org
 * deployed with NO alert config, `checkDeliverability` short-circuited on the
 * missing record, and no alert could fire on any real install. Defaults make it
 * fire; the operator tunes or disables it.
 *
 * The numbers follow the thresholds mailbox providers actually act on — Google
 * Postmaster treats a 0.3% complaint rate as the level to stay under, so the
 * warning sits there and the critical alert at 0.5%. Bounce rates above 5% put
 * an SES account under review, so warn at 5% and go critical at 10%.
 */
export const DEFAULT_ALERT_RULES = [
    { metric: "complaint_rate", warnAt: 0.003, haltAt: 0.005, enabled: true },
    { metric: "bounce_rate", warnAt: 0.05, haltAt: 0.1, enabled: true },
    // Informational only until a live reputation signal exists (metricValue
    // returns 0), so it is off — an always-zero rule would never fire anyway and
    // leaving it enabled implies a signal we do not have.
    { metric: "reputation", warnAt: 0, haltAt: 0, enabled: false },
];
/** The config a newly provisioned org gets, so protection is never opt-in. */
export function defaultAlertConfig(orgId, snsTopicArn) {
    return {
        orgId,
        snsTopicArn,
        rules: DEFAULT_ALERT_RULES.map((r) => ({ ...r })),
        notifyTargets: [],
    };
}
/** A one-line, human-readable account of the breaches, for the alert email. */
export function describeBreaches(breaches) {
    const pct = (v) => `${(v * 100).toFixed(v < 0.01 ? 2 : 1)}%`;
    return breaches
        .map((b) => {
        const name = b.metric.replace(/_/g, " ");
        const shown = b.metric === "send_failures" || b.metric === "reputation"
            ? `${b.value} (threshold ${b.threshold})`
            : `${pct(b.value)} (threshold ${pct(b.threshold)})`;
        return `${b.level === "critical" ? "CRITICAL" : "Warning"}: ${name} ${shown}`;
    })
        .join("; ");
}
/**
 * Load the org's alert config + the campaign's counters, evaluate, and publish
 * any NEW breach level to the org's SNS topic. Never changes the campaign or
 * stops the send (#366). No config → no-op. Safe to call after every bounce and
 * complaint: each level is published at most once per campaign id.
 */
export async function checkDeliverability(stores, publisher, clock, orgId, campaignId) {
    const config = await stores.alerts.get(orgId);
    if (!config)
        return { breaches: [], notified: [] };
    // Read the STORED counters rather than folding the whole event log (#221,
    // #182). This runs on every bounce and complaint, so the old full scan made
    // the cost of evaluating deliverability grow with the campaign's own event
    // history — worst exactly when a campaign is generating the most events.
    // Counters are maintained transactionally with each append, so they are
    // exact rather than approximate.
    //
    // Three sources, cheapest first (#293):
    //  1. the campaign record's counters — normal campaigns and recurring editions;
    //  2. the SENDID# counters — drip steps, re-engagement steps and any other id
    //     that mints a campaignId but never gets a record. These used to fall
    //     straight through to the fold below, so deliverability evaluation for
    //     them was O(events) on EVERY bounce and complaint;
    //  3. folding the event log, still correct and still needed for ids whose
    //     events predate the SENDID# index.
    const campaign = await stores.campaigns.get(orgId, campaignId);
    const counters = campaign?.counters ??
        (await stores.sendIdCounters?.(orgId, campaignId)) ??
        deriveCounters(await stores.events.all(orgId, campaignId));
    const breaches = evaluateAlerts(config, counters);
    // No topic, nobody to tell. Nothing is claimed either, so an org that adds a
    // topic later still hears about a campaign that is still breaching. The
    // console's Deliverability screen warns that this org is unmonitored.
    if (breaches.length === 0 || !config.snsTopicArn)
        return { breaches, notified: [] };
    const at = clock.now().toISOString();
    const levels = [...new Set(breaches.map((b) => b.level))];
    // Once per campaign and level, not once per bounce: a breaching campaign
    // produces a bounce event per bad address, and each one lands here. The claim
    // is a conditional write so concurrent events in one batch can't both win.
    // Without the store (older fakes) every breach is published, as before.
    const fresh = [];
    for (const level of levels) {
        if (!stores.alertNotices || (await stores.alertNotices.claim(orgId, campaignId, level, at)))
            fresh.push(level);
    }
    if (fresh.length === 0)
        return { breaches, notified: [] };
    const severity = fresh.includes("critical") ? "critical" : "warning";
    const shown = breaches.filter((b) => fresh.includes(b.level));
    try {
        await publisher.publish(config.snsTopicArn, {
            orgId,
            campaignId,
            at,
            severity,
            summary: `${campaignId}: ${describeBreaches(shown)}. The send has not been stopped.`,
            breaches: shown,
        });
    }
    catch (e) {
        // Give the claims back so the next bounce or complaint tries again; a
        // swallowed failure here would mean this level is never reported.
        for (const level of fresh)
            await stores.alertNotices?.release(orgId, campaignId, level);
        console.error("alerts: publish failed; will retry on the next event", {
            orgId,
            campaignId,
            error: e.message,
        });
        return { breaches, notified: [] };
    }
    return { breaches, notified: fresh };
}
//# sourceMappingURL=alerts.js.map